/**
 * The mode buttons talk about the language you are actually writing in.
 *
 * Half the modes rewrite text inside one language rather than translating
 * between two (`followsInputLanguage`), so "A1 English" was a label that told a
 * Korean user the wrong thing about what the button would do. The registry now
 * writes `A1 {lang}` and this fills the blank in, from whatever the source
 * picker says or the detector has found.
 *
 * Kept out of the components because two of them need the same substitution —
 * the tooltip heading and the `aria-label` on the button — and a placeholder
 * left unfilled in one of them is exactly the kind of thing nobody notices.
 */

/** What the app currently believes the user is writing in. */
export interface InputLanguage {
  code: string;
  name: string;
  /** ISO-3166 code for the flag, or null when the sprite has none. */
  country: string | null;
}

/**
 * The button's title.
 *
 * With no language known — an empty box, or Latin text the detector has not
 * resolved yet — the placeholder is dropped rather than filled with a stand-in:
 * "A1" is a fine title on its own, while "A1 your language" reads like a bug.
 */
export function modeLabel(label: string, lang: string | null | undefined): string {
  return label
    .replace(/\{lang\}/g, lang ?? '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * The one-sentence description under it.
 *
 * Here the placeholder DOES get a stand-in, because the sentence is built around
 * it — "Checks your writing in and shows…" is broken in a way a title is not.
 *
 * Which puts one rule on whoever writes a hint in the registry: **`{lang}` must
 * follow a preposition** ("in {lang}", "into {lang}"), never a possessive. The
 * first draft said "Finds the mistakes in your {lang}", and before detection
 * resolves that reads "the mistakes in your your language" — visible for the
 * first second of every visit, which is the second people read the tooltips in.
 */
export function modeHint(hint: string, lang: string | null | undefined): string {
  return hint.replace(/\{lang\}/g, lang ?? 'the language you type in');
}
